import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import MovieIcon from '@mui/icons-material/Movie';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ProgressModal, finishProcessing, useIframeResizer } from './common';

interface ImportURLState {
  url: string;
  isImporting: boolean;
  progress: number;
  previewType:
    | 'youtube'
    | 'facebook'
    | 'linkedin'
    | 'instagram'
    | 'twitter'
    | 'vimeo'
    | 'file'
    | null;
  previewId: string | null;
  thumbnailUrl: string | null;
  title: string | null;
}

const URL_PATTERNS = {
  youtube: {
    patterns: [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ],
    getThumbnail: (id: string) =>
      `https://img.youtube.com/vi/${id}/default.jpg`,
    getTitle: async (id: string) => {
      try {
        const response = await fetch(
          `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`
        );
        const data = await response.json();
        return data.title || null;
      } catch {
        return null;
      }
    },
  },
  facebook: {
    patterns: [/facebook\.com\/[^/]+\/videos\/(\d+)/, /fb\.watch\/([^/]+)/],
  },
  linkedin: {
    patterns: [
      /linkedin\.com\/posts\/([^/]+)/,
      /linkedin\.com\/feed\/update\/([^/]+)/,
    ],
  },
  instagram: {
    patterns: [/instagram\.com\/p\/([^/]+)/, /instagram\.com\/reel\/([^/]+)/],
  },
  twitter: {
    patterns: [/(?:twitter|x)\.com\/\w+\/status\/(\d+)/],
  },
  vimeo: {
    patterns: [/vimeo\.com\/(\d+)/],
  },
  file: {
    patterns: [/\.(mp4|mov|avi|wmv|flv|mkv|webm|mp3|wav|ogg|m4a)$/i],
  },
};

const extractUrlInfo = async (
  url: string
): Promise<{
  type: ImportURLState['previewType'];
  id: string | null;
  title: string | null;
}> => {
  const matchedPlatform = Object.entries(URL_PATTERNS).find(([, config]) =>
    config.patterns.some((pattern) => {
      const match = url.match(pattern);
      if (match) {
        return true;
      }
      return false;
    })
  );

  if (matchedPlatform) {
    const [platform, config] = matchedPlatform;
    const pattern = config.patterns.find((p) => url.match(p));
    const match = url.match(pattern!);
    const id = match![1] || url;
    let title = null;

    if (platform === 'youtube' && id) {
      title = await URL_PATTERNS.youtube.getTitle(id);
    }

    return {
      type: platform as ImportURLState['previewType'],
      id,
      title,
    };
  }

  return { type: null, id: null, title: null };
};

const getPlatformIcon = (type: ImportURLState['previewType']) => {
  switch (type) {
    case 'youtube':
      return <YouTubeIcon sx={{ fontSize: 16, color: '#FF0000' }} />;
    case 'facebook':
      return <FacebookIcon sx={{ fontSize: 16, color: '#1877F2' }} />;
    case 'linkedin':
      return <LinkedInIcon sx={{ fontSize: 16, color: '#0A66C2' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ fontSize: 16, color: '#E4405F' }} />;
    case 'twitter':
      return <TwitterIcon sx={{ fontSize: 16, color: '#1DA1F2' }} />;
    case 'vimeo':
      return <MovieIcon sx={{ fontSize: 16, color: '#1AB7EA' }} />;
    case 'file':
      return <InsertDriveFileIcon sx={{ fontSize: 16, color: '#607D8B' }} />;
    default:
      return null;
  }
};

const getPlatformLabel = (type: ImportURLState['previewType']) => {
  const { t } = useTranslation();
  switch (type) {
    case 'youtube':
      return t('importURL.platforms.youtube');
    case 'facebook':
      return t('importURL.platforms.facebook');
    case 'linkedin':
      return t('importURL.platforms.linkedin');
    case 'instagram':
      return t('importURL.platforms.instagram');
    case 'twitter':
      return t('importURL.platforms.twitter');
    case 'vimeo':
      return t('importURL.platforms.vimeo');
    case 'file':
      return t('importURL.platforms.file');
    default:
      return '';
  }
};

const ImportURL: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [state, setState] = useState<ImportURLState>({
    url: '',
    isImporting: false,
    progress: 0,
    previewType: null,
    previewId: null,
    thumbnailUrl: null,
    title: null,
  });

  useEffect(() => {
    const fetchUrlInfo = async () => {
      const { type, id, title } = await extractUrlInfo(state.url);
      if (type && id) {
        setState((prev) => ({
          ...prev,
          previewType: type,
          previewId: id,
          thumbnailUrl:
            type === 'youtube' ? URL_PATTERNS.youtube.getThumbnail(id) : null,
          title,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          previewType: null,
          previewId: null,
          thumbnailUrl: null,
          title: null,
        }));
      }
    };
    fetchUrlInfo();
  }, [state.url]);

  const handleUrlImport = () => {
    if (state.url) {
      setState((prev) => ({ ...prev, isImporting: true, progress: 0 }));
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 10;
        setState((prev) => ({
          ...prev,
          progress: Math.min(currentProgress, 100),
        }));
        if (currentProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            finishProcessing(state.url);
          }, 2000);
        }
      }, 20);
    }
  };

  const handleReset = () => {
    setState({
      url: '',
      isImporting: false,
      progress: 0,
      previewType: null,
      previewId: null,
      thumbnailUrl: null,
      title: null,
    });
  };

  const handleSaveAgain = () => {
    if (state.url) {
      setState((prev) => ({ ...prev, isImporting: true, progress: 0 }));
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 2;
        setState((prev) => ({
          ...prev,
          progress: Math.min(currentProgress, 100),
        }));
        if (currentProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            finishProcessing(state.url);
          }, 2000);
        }
      }, 50);
    }
  };

  const handleDismissPreview = () => {
    setState((prev) => ({
      ...prev,
      url: '',
      previewType: null,
      previewId: null,
      thumbnailUrl: null,
      title: null,
    }));
  };

  const getPreviewPadding = () => {
    if (!state.previewType) return '14px';
    if (isSmallScreen || isMediumScreen) return '48px';
    return '150px';
  };

  const getPreviewMaxWidth = () => {
    if (isSmallScreen || isMediumScreen) return '32px';
    return state.title ? '250px' : '130px';
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ p: 3, maxWidth: '60rem', width: '100%' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={9}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                placeholder={t('importURL.placeholder')}
                value={state.url}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, url: e.target.value }))
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    pr: getPreviewPadding(),
                  },
                }}
              />
              {state.previewType && (
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 0.5,
                    pr: isSmallScreen || isMediumScreen ? 0.5 : 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: '4px',
                    maxWidth: getPreviewMaxWidth(),
                    height: '32px',
                    overflow: 'hidden',
                    zIndex: 1,
                    ...((isSmallScreen || isMediumScreen) && {
                      '&:hover': {
                        '.close-button': {
                          opacity: 1,
                        },
                      },
                    }),
                  }}
                >
                  {state.thumbnailUrl ? (
                    <Box
                      component="img"
                      src={state.thumbnailUrl}
                      alt={`${state.previewType} thumbnail`}
                      sx={{
                        width: '32px',
                        height: '32px',
                        objectFit: 'cover',
                        borderRadius: '2px',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: '2px',
                      }}
                    >
                      {getPlatformIcon(state.previewType)}
                    </Box>
                  )}
                  {!isSmallScreen && !isMediumScreen && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '10px',
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {state.title || getPlatformLabel(state.previewType)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleDismissPreview}
                        sx={{
                          p: 0.5,
                          mr: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                  {(isSmallScreen || isMediumScreen) && (
                    <IconButton
                      className="close-button"
                      size="small"
                      onClick={handleDismissPreview}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '20px',
                        height: '20px',
                        p: 0,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14, color: 'inherit' }} />
                    </IconButton>
                  )}
                </Paper>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleUrlImport}
              disabled={!state.url || state.isImporting}
              sx={{
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('importURL.importButton')}
            </Button>
          </Grid>
        </Grid>

        <ProgressModal
          open={state.isImporting}
          progress={state.progress}
          isComplete={state.progress === 100}
          processingMessage={t('importURL.processing')}
          completeMessage={t('importURL.complete')}
          onReset={handleReset}
          onSaveAgain={handleSaveAgain}
        />
      </Box>
    </Box>
  );
};

export default ImportURL;
